/**
 * API Keys — admin-only Settings tab.
 * Shows organisation-scoped developer API keys used for programmatic access
 * to the admin REST API. Keys are single-reveal: the raw secret is displayed
 * once at creation. Subsequent views show only a masked prefix.
 *
 * Permission gate: api_keys.manage (OrgAdmin + Admin roles).
 */
import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth, hasPermission } from "@/lib/auth";
import { PageShell } from "@/components/page-shell";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Key, Plus, Trash2, Copy, AlertTriangle, Eye, EyeOff, ShieldAlert } from "lucide-react";

type ApiKeyDto = {
  id: string;
  name: string;
  prefix: string;
  createdByName?: string | null;
  createdAt: string;
  lastUsedAt?: string | null;
  isActive: boolean;
};

type ApiKeyCreatedDto = {
  id: string;
  name: string;
  prefix: string;
  secret: string;
  createdAt: string;
};

export default function ApiKeysPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [newName, setNewName] = useState("");
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<ApiKeyCreatedDto | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (!hasPermission(user, "api_keys.manage")) {
    return (
      <PageShell title="API Keys">
        <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="forbidden-api-keys">
          <ShieldAlert className="w-4 h-4" />
          You don't have permission to manage API keys.
        </div>
      </PageShell>
    );
  }

  const keys = useQuery<ApiKeyDto[]>({
    queryKey: ["/api/org-api-keys"],
    queryFn: async () => (await apiRequest("GET", "/api/org-api-keys")).json(),
  });

  const create = useMutation({
    mutationFn: async () =>
      (await apiRequest("POST", "/api/org-api-keys", { name: newName })).json() as Promise<ApiKeyCreatedDto>,
    onSuccess: (data) => {
      setNewName("");
      setRevealed(data);
      setShowSecret(true);
      setDismissed(false);
      queryClient.invalidateQueries({ queryKey: ["/api/org-api-keys"] });
    },
    onError: (e: any) =>
      toast({ title: "Failed to create key", description: e.message, variant: "destructive" }),
  });

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/org-api-keys/${id}`);
    },
    onSuccess: () => {
      setRevokingId(null);
      queryClient.invalidateQueries({ queryKey: ["/api/org-api-keys"] });
      toast({ title: "API key revoked" });
    },
    onError: (e: any) =>
      toast({ title: "Revoke failed", description: e.message, variant: "destructive" }),
  });

  const copySecret = () => {
    if (!revealed?.secret) return;
    navigator.clipboard.writeText(revealed.secret);
    toast({ title: "Secret copied to clipboard" });
  };

  return (
    <PageShell title="API Keys" subtitle="Manage developer API keys for programmatic access to the admin REST API.">
      <div className="space-y-6 max-w-2xl" data-testid="page-api-keys">

        {/* Single-reveal banner */}
        {revealed && !dismissed && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 space-y-3" data-testid="new-key-banner">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold text-amber-800 text-sm">Save your API key now</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  This is the only time the secret will be shown. It cannot be retrieved again.
                </p>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <code
                className={`flex-1 bg-white border rounded px-3 py-2 text-sm font-mono break-all select-all ${!showSecret ? "blur-sm" : ""}`}
                data-testid="revealed-secret"
              >
                {revealed.secret}
              </code>
              <button
                onClick={() => setShowSecret((v) => !v)}
                className="p-2 rounded hover:bg-amber-100 text-amber-700"
                title={showSecret ? "Hide" : "Show"}
                data-testid="button-toggle-secret"
              >
                {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
              <Button size="sm" variant="outline" onClick={copySecret} data-testid="button-copy-secret">
                <Copy className="w-3.5 h-3.5 mr-1" />Copy
              </Button>
            </div>
            <Button
              size="sm" variant="ghost"
              onClick={() => { setDismissed(true); setRevealed(null); }}
              data-testid="button-dismiss-banner"
            >
              I've saved the key
            </Button>
          </div>
        )}

        {/* Create new key */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Key className="w-4 h-4" />Create API key</CardTitle>
            <CardDescription>Give the key a descriptive name (e.g. "CI pipeline", "CRM integration").</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Key name"
                maxLength={100}
                data-testid="input-new-key-name"
                onKeyDown={(e) => e.key === "Enter" && newName.trim() && create.mutate()}
              />
              <Button
                onClick={() => create.mutate()}
                disabled={!newName.trim() || create.isPending}
                data-testid="button-create-key"
              >
                <Plus className="w-4 h-4 mr-1" />
                {create.isPending ? "Creating…" : "Create"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Active keys list */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Active keys</CardTitle>
            <CardDescription>
              Only the prefix is shown. Revoke a key immediately if it is compromised.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {keys.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
            {!keys.isLoading && (keys.data || []).length === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="text-no-keys">No active API keys.</p>
            )}
            <div className="space-y-2" data-testid="list-api-keys">
              {(keys.data || []).map((k) => (
                <div
                  key={k.id}
                  className="flex items-center justify-between gap-3 rounded-lg border px-4 py-3"
                  data-testid={`key-row-${k.id}`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{k.name}</span>
                      <Badge variant="outline" className="text-xs font-mono">{k.prefix}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5 space-x-2">
                      <span>Created {new Date(k.createdAt).toLocaleDateString()}</span>
                      {k.createdByName && <span>by {k.createdByName}</span>}
                      {k.lastUsedAt && <span>· Last used {new Date(k.lastUsedAt).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive shrink-0"
                    onClick={() => setRevokingId(k.id)}
                    data-testid={`button-revoke-${k.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />Revoke
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4">
            <p className="text-xs text-muted-foreground space-y-1">
              <span className="block font-medium">Using API keys</span>
              Pass the key as a Bearer token: <code className="bg-muted px-1 py-0.5 rounded text-xs">Authorization: Bearer sk-org-…</code>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Confirm revoke */}
      <AlertDialog open={!!revokingId} onOpenChange={(o) => !o && setRevokingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke API key?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently revoke the key. Any application using it will lose access immediately.
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() => revokingId && revoke.mutate(revokingId)}
              data-testid="button-confirm-revoke"
            >
              Revoke
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}
